import React, { useContext, useState } from "react"
import { FormattedMessage } from "react-intl"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons"
import Modal from "react-bootstrap/Modal"
import { FirebaseContext } from "../../components/Firebase"
import { sendStandardToast } from "../../components/toasts/standard-toast"
import SubmitButton from "../portfolio/SubmitButton"
import ReauthenticateModal from "./ReauthenticateModal"
import AddPasswordModal from "./AddPasswordModal"
import "./account.css"

/**
 * Button for adding a personal email to a user's account
 */
const AddPersonalEmailButton = () => {
  const { firebase } = useContext(FirebaseContext)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [inputtedEmail, setInputtedEmail] = useState("")
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showReauthModal, setShowReauthModal] = useState(false)

  // If the user doesn't have a password associated with their account before
  // updating their primary email address, then their school email credential
  // may be removed from their account the first time they use their personal
  // email to sign in)
  const userHasPassword = firebase?.auth?.currentUser?.providerData?.some(
    p => p.providerId === "password"
  )

  const handleClick = (e) => {
    e.preventDefault()
    if (userHasPassword) {
      setEmailModalOpen(true)
    } else {
      setShowPasswordModal(true)
    }
  }

  const onEmailModalClose = () => {
    setEmailModalOpen(false)
    setInputtedEmail("")
    setShowPasswordModal(false)
    setShowReauthModal(false)
  }

  const afterPasswordAdded = () => {
    setShowPasswordModal(false)
    setEmailModalOpen(true)
  }

  const sendVerificationEmail = async (email) => {
    // 1) Confirm that the provided email isn't already in use by a different account
    try {
      if (await firebase.checkIfEmailAlreadyUsedAsSecondary(email)) {
        sendStandardToast({
          heading: `The email ${email} is already in use by a different Wingspans account`,
        })
        return
      }
    } catch (e) {
      sendStandardToast({
        heading: `Encountered an error when checking if ${email} is in use by a different Wingspans account`,
        message: "If this issue persists, please contact Wingspans support"
      })
      return
    }

    // 2) Send the email update link to the provided email
    try {
      await firebase.auth.currentUser.verifyBeforeUpdateEmail(email)
    } catch (e) {
      if (e.code === "auth/invalid-new-email") {
        sendStandardToast({
          heading: `${email} is not a valid email`,
        })
      } else if (e.code === "auth/requires-recent-login") {
        setShowReauthModal(true)
        setEmailModalOpen(false)
        return
      } else if (e.code === "auth/email-already-in-use") {
        sendStandardToast({
          heading: `The email ${email} is already in use by a different Wingspans account`,
        })
      } else {
        console.log("error during verifyBeforeUpdateEmail", e.code, e)
        sendStandardToast({
          heading: `Error when trying to send verification link to ${email}`,
          message: e.message,
        })
      }
      setEmailModalOpen(true)
      
      return
    }

    sendStandardToast({
      heading: `A verification link has been sent to ${email}`,
      message: `Clicking that link will add the email ${email} to your account`,
      type: "success",
    })
    onEmailModalClose()
  }

  return (
    <>
      <button
        className="account-page-submit-button"
        onClick={handleClick}
        type="button"
      >
        <FontAwesomeIcon icon={faEnvelope} size="lg" />
        <FormattedMessage
          id="account-add-personal-email"
          defaultMessage="Add Personal Email"
        />
      </button>

      <Modal show={emailModalOpen} onHide={onEmailModalClose} size="md">
        <Modal.Header closeButton>
          <div className="eyebrow component-eyebrow">
            <FormattedMessage id="account-page-enter-personal-email" defaultMessage="Enter Personal Email" />
          </div>
        </Modal.Header>
        <Modal.Body closeButton>
          <input type="text" value={inputtedEmail} onChange={e => setInputtedEmail(e.target.value)} />
        </Modal.Body>    
        <Modal.Footer>
          <SubmitButton
            label={<FormattedMessage id="account-page-send-link" defaultMessage="Send Verification Link" />}
            onClick={() => sendVerificationEmail(inputtedEmail)}
            className="account-page-submit-button"
          />
        </Modal.Footer>
      </Modal>

      <AddPasswordModal
        show={showPasswordModal}
        handleClose={() => setShowPasswordModal(false)}
        handleOpen={() => setShowPasswordModal(true)}
        afterPasswordAdded={afterPasswordAdded}
      />

      <ReauthenticateModal
        show={showReauthModal}
        handleClose={() => setShowReauthModal(false)}
        onReauthSuccess={async () => sendVerificationEmail(inputtedEmail)}
      />
    </>
  )
}

export default AddPersonalEmailButton
